import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useState } from 'react';
import { getAxiosInstance } from '../axios/axios.utilities';
import type { AxiosInstance } from 'axios';

type ApiProviderProps = {
  queryClient?: QueryClient;
  axiosInstance?: AxiosInstance;
  children: ReactNode;
};

const AxiosInstanceContext = createContext<AxiosInstance | null>(null);

/** React Query provider with a required QueryClient and optional Axios instance. */
export function ApiProvider({ queryClient: queryClientProp, axiosInstance, children }: ApiProviderProps) {
  // Create a single QueryClient instance per provider to avoid unnecessary re-renders.
  const [defaultQueryClient] = useState(() => new QueryClient());
  const queryClient = queryClientProp ?? defaultQueryClient;
  const instance = axiosInstance ?? getAxiosInstance();

  return (
    <AxiosInstanceContext.Provider value={instance}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AxiosInstanceContext.Provider>
  );
}

/** Access the Axios instance from ApiProvider. */
export function useAxiosInstance(): AxiosInstance {
  const axiosInstance = useContext(AxiosInstanceContext);
  if (!axiosInstance) {
    throw new Error('useAxiosInstance must be used within an <ApiProvider>');
  }
  return axiosInstance;
}
